import { generateMultiTonePalette } from './palette.js';
import { applyPackageToLayout } from './packageRecipe.js';
import { emailSlugFromName } from './slug.js';
import { typographyForVibe } from './typography.js';
import type {
  BusinessInfo,
  Character,
  Density,
  FaqItem,
  LayoutShell,
  LeadRecord,
  Personality,
  PricingTier,
  ReviewBadge,
  Section,
  Service,
  SiteDNA,
  TestimonialItem,
  TrustRow,
  ValuePropItem,
  Vibe,
} from './zod.js';

/** The four character chips offered on the call sheet. Up to MAX_CHARACTERS at once. */
export const CHARACTERS: readonly Character[] = ['Warm', 'Formal', 'Modern', 'Heritage'];

/** The sheet says "pick up to 2" — 0, 1, or 2 are all valid. */
export const MAX_CHARACTERS = 2;

export const DENSITIES: readonly Density[] = ['quiet', 'mid', 'bold'];

export const DENSITY_LABELS: Readonly<Record<Density, string>> = {
  quiet: 'Quieter',
  mid: 'Balanced',
  bold: 'Bolder',
};

export type IndustryBucket =
  | 'trades'
  | 'professional'
  | 'wellness'
  | 'hospitality'
  | 'legal-finance'
  | 'insurance-adjusting'
  | 'creative'
  | 'default';

export interface LookPick {
  vibe: Vibe;
  layoutShell: LayoutShell;
  sectionOrder: Section[];
  ctaDefault: string;
  label: string;
  density: Density;
  bucket: IndustryBucket;
  presetId: string;
  presetName: string;
  /** Character chips with legacy Quiet/Bold filtered out. */
  characters: Character[];
}

/**
 * A curated, named look for an industry bucket: vibe + shell + a default
 * loudness. The three presets per bucket are the combinations that must be
 * beautiful; density and character modifiers compose on top of them.
 */
export interface LookPreset {
  id: string;
  name: string;
  blurb: string;
  vibe: Vibe;
  shell: LayoutShell;
  defaultDensity: Density;
}

export interface PlaceholderPack {
  headline: string;
  tagline: string;
  valueProp: string;
  ctaLabel: string;
  phone: string;
  email: string;
  serviceArea: string;
  proofChips: string[];
  services: Service[];
  faqs: FaqItem[];
  testimonials: TestimonialItem[];
  valueProps: ValuePropItem[];
  pricingTiers: PricingTier[];
  reviewBadges: ReviewBadge[];
  trustRow: TrustRow;
}

const KEYWORDS: ReadonlyArray<{ bucket: IndustryBucket; needles: readonly string[] }> = [
  {
    bucket: 'insurance-adjusting',
    needles: ['insurance', 'adjuster', 'claim', 'public adjust', 'storm damage', 'roof claim'],
  },
  {
    bucket: 'legal-finance',
    needles: ['law', 'legal', 'attorney', 'accountant', 'accounting', 'cpa', 'tax', 'real estate', 'realtor'],
  },
  {
    bucket: 'trades',
    needles: [
      'plumb',
      'hvac',
      'electrical',
      'roof',
      'landscap',
      'clean',
      'construct',
      'automotive',
      'hvac',
      'handyman',
      'remodel',
      'paint',
    ],
  },
  {
    bucket: 'wellness',
    needles: [
      'dental',
      'medical',
      'chiro',
      'vet',
      'salon',
      'barber',
      'fitness',
      'yoga',
      'spa',
      'wellness',
      'therapy',
      'clinic',
    ],
  },
  {
    bucket: 'hospitality',
    needles: ['restaurant', 'cafe', 'bakery', 'cater', 'bar ', 'bistro', 'food'],
  },
  {
    bucket: 'creative',
    needles: ['photo', 'wedding', 'studio', 'design', 'film', 'video'],
  },
  {
    bucket: 'professional',
    needles: ['consult', 'marketing', 'saas', 'agency', 'software', 'coach'],
  },
];

export function industryBucket(industry: string | undefined): IndustryBucket {
  const hay = (industry ?? '').toLowerCase();
  if (!hay.trim()) return 'default';
  for (const row of KEYWORDS) {
    if (row.needles.some((n) => hay.includes(n))) return row.bucket;
  }
  return 'default';
}

const PRESETS: Readonly<Record<IndustryBucket, readonly LookPreset[]>> = {
  trades: [
    { id: 'workhorse', name: 'Workhorse', blurb: 'Straightforward and sturdy — the crew that shows up.', vibe: 'The Bold', shell: 'stack', defaultDensity: 'mid' },
    { id: 'letterhead', name: 'Letterhead', blurb: 'Licensed-and-insured on good paper.', vibe: 'The Professional', shell: 'letterhead', defaultDensity: 'quiet' },
    { id: 'job-board', name: 'Job Board', blurb: 'Big type, posted rates, call today.', vibe: 'The Bold', shell: 'marquee', defaultDensity: 'bold' },
  ],
  professional: [
    { id: 'front-office', name: 'Front Office', blurb: 'Clean reception, clear thinking.', vibe: 'The Modern', shell: 'foyer', defaultDensity: 'mid' },
    { id: 'letterhead', name: 'Letterhead', blurb: 'Quiet confidence, in writing.', vibe: 'The Professional', shell: 'letterhead', defaultDensity: 'quiet' },
    { id: 'briefing', name: 'Briefing Deck', blurb: 'Dense, organized, decisive.', vibe: 'The Tech', shell: 'cabinet', defaultDensity: 'bold' },
  ],
  wellness: [
    { id: 'morning-light', name: 'Morning Light', blurb: 'Soft air and unhurried space.', vibe: 'The Wellness', shell: 'atrium', defaultDensity: 'quiet' },
    { id: 'private-suite', name: 'Private Suite', blurb: 'Considered, appointment-only calm.', vibe: 'The Luxe', shell: 'letterhead', defaultDensity: 'mid' },
    { id: 'open-studio', name: 'Open Studio', blurb: 'Hands-on, textured, alive.', vibe: 'The Artisan', shell: 'vitrine', defaultDensity: 'bold' },
  ],
  hospitality: [
    { id: 'warm-room', name: 'Warm Room', blurb: 'The neighborhood table, lit well.', vibe: 'The Artisan', shell: 'vitrine', defaultDensity: 'mid' },
    { id: 'menu-card', name: 'Menu Card', blurb: 'A short menu on good paper.', vibe: 'The Wellness', shell: 'atrium', defaultDensity: 'quiet' },
    { id: 'marquee', name: 'Marquee', blurb: 'Night out — lights on, doors open.', vibe: 'The Luxe', shell: 'marquee', defaultDensity: 'bold' },
  ],
  'legal-finance': [
    { id: 'letterhead', name: 'Letterhead', blurb: 'Counsel on engraved stock.', vibe: 'The Professional', shell: 'letterhead', defaultDensity: 'quiet' },
    { id: 'ledger', name: 'Ledger', blurb: 'Ruled lines and plain numbers.', vibe: 'The Editorial', shell: 'ledger', defaultDensity: 'mid' },
    { id: 'chambers', name: 'Chambers', blurb: 'Wood-paneled weight and history.', vibe: 'The Heritage', shell: 'foyer', defaultDensity: 'bold' },
  ],
  'insurance-adjusting': [
    { id: 'field-office', name: 'Field Office', blurb: 'Organized and on your side.', vibe: 'The Professional', shell: 'foyer', defaultDensity: 'mid' },
    { id: 'claim-file', name: 'Claim File', blurb: 'Documented, stamped, defensible.', vibe: 'The Heritage', shell: 'letterhead', defaultDensity: 'quiet' },
    { id: 'storm-line', name: 'Storm Line', blurb: 'Urgent, visible, first on the roof.', vibe: 'The Bold', shell: 'marquee', defaultDensity: 'bold' },
  ],
  creative: [
    { id: 'gallery-wall', name: 'Gallery Wall', blurb: 'Work first, frames straight.', vibe: 'The Artisan', shell: 'vitrine', defaultDensity: 'mid' },
    { id: 'contact-sheet', name: 'Contact Sheet', blurb: 'Selects, captions, white space.', vibe: 'The Editorial', shell: 'letterhead', defaultDensity: 'quiet' },
    { id: 'open-reel', name: 'Open Reel', blurb: 'Motion, montage, volume up.', vibe: 'The Modern', shell: 'drift', defaultDensity: 'bold' },
  ],
  default: [
    { id: 'storefront', name: 'Storefront', blurb: 'Open sign on, counter wiped.', vibe: 'The Modern', shell: 'foyer', defaultDensity: 'mid' },
    { id: 'letterhead', name: 'Letterhead', blurb: 'Plain-spoken and put together.', vibe: 'The Professional', shell: 'letterhead', defaultDensity: 'quiet' },
    { id: 'billboard', name: 'Billboard', blurb: 'Big promise, plain route to call.', vibe: 'The Bold', shell: 'stack', defaultDensity: 'bold' },
  ],
};

/** Named presets for an industry (resolved through its bucket). */
export function lookPresets(industry: string | undefined): readonly LookPreset[] {
  return PRESETS[industryBucket(industry)];
}

const SECTIONS: Readonly<Record<IndustryBucket, Section[]>> = {
  trades: ['Hero', 'Services', 'SocialProof', 'FAQ', 'Contact'],
  professional: ['Hero', 'Services', 'ValueProps', 'FAQ', 'Contact'],
  wellness: ['Hero', 'ValueProps', 'Services', 'Testimonials', 'Contact'],
  hospitality: ['Hero', 'Services', 'Gallery', 'FAQ', 'Contact'],
  'legal-finance': ['Hero', 'ValueProps', 'Services', 'FAQ', 'Contact'],
  'insurance-adjusting': ['Hero', 'ValueProps', 'Services', 'FAQ', 'Contact'],
  creative: ['Hero', 'Gallery', 'Services', 'Testimonials', 'Contact'],
  default: ['Hero', 'Services', 'ValueProps', 'FAQ', 'Contact'],
};

const CTAS: Readonly<Record<IndustryBucket, string>> = {
  trades: 'Call now',
  professional: 'Book a consult',
  wellness: 'Book now',
  hospitality: 'Reserve a table',
  'legal-finance': 'Schedule a consult',
  'insurance-adjusting': 'Free claim review',
  creative: 'Check availability',
  default: 'Get in touch',
};

/** Character chips with legacy Quiet/Bold filtered out. */
export function characterChips(personality: readonly Personality[]): Character[] {
  return personality.filter((p): p is Character => p !== 'Quiet' && p !== 'Bold');
}

export type CopyRegister = 'standard' | 'formal';

/** Formal and Heritage chips shift the copy voice, not just the paint. */
export function copyRegisterFor(personality: readonly Personality[]): CopyRegister {
  const chars = characterChips(personality);
  return chars.includes('Formal') || chars.includes('Heritage') ? 'formal' : 'standard';
}

interface RegisterOverride {
  headline: string;
  tagline: string;
  ctaLabel?: string;
}

/**
 * Formal-register voice per bucket. Only the loudest copy slots swap —
 * headline, tagline, CTA. Body copy stays in the standard industry pack.
 */
const FORMAL_REGISTER: Partial<Record<IndustryBucket, RegisterOverride>> = {
  hospitality: {
    headline: 'An evening, properly set.',
    tagline: 'A composed room. A considered menu.',
    ctaLabel: 'Request a reservation',
  },
  trades: {
    headline: 'Done right. Done once.',
    tagline: 'Licensed, insured, on schedule.',
    ctaLabel: 'Request a visit',
  },
  'legal-finance': {
    headline: 'Counsel for decisions that matter.',
    tagline: 'Measured advice. Documented outcomes.',
    ctaLabel: 'Request a consultation',
  },
  wellness: {
    headline: 'Care, without the waiting room.',
    tagline: 'Quiet rooms. Considered care.',
    ctaLabel: 'Request an appointment',
  },
  professional: {
    headline: 'Judgment you can put in writing.',
    tagline: 'Senior counsel. Working documents.',
    ctaLabel: 'Request a briefing',
  },
  'insurance-adjusting': {
    headline: 'Your claim, properly represented.',
    tagline: 'Licensed adjusters. Documented losses.',
    ctaLabel: 'Request a claim review',
  },
  creative: {
    headline: 'Commissioned work, kept simple.',
    tagline: 'Considered images. Kept promises.',
    ctaLabel: 'Request availability',
  },
  default: {
    headline: 'The obvious next call, made simple.',
    tagline: 'Clear terms. Straight answers.',
    ctaLabel: 'Request an introduction',
  },
};

/** Legacy Quiet/Bold personality chips map onto density when no explicit density is set. */
export function densityFromLegacyPersonality(personality: readonly Personality[]): Density | undefined {
  const quiet = personality.includes('Quiet');
  const bold = personality.includes('Bold');
  if (quiet && !bold) return 'quiet';
  if (bold && !quiet) return 'bold';
  return undefined;
}

export function resolveLook(
  industry: string | undefined,
  personality: readonly Personality[] = [],
  lookVariant = 0,
  density?: Density,
): LookPick {
  const bucket = industryBucket(industry);
  const presets = PRESETS[bucket];
  const n = presets.length || 1;
  const idx = ((lookVariant % n) + n) % n;
  const preset = presets[idx] ?? PRESETS.default[0]!;
  const resolvedDensity = density ?? densityFromLegacyPersonality(personality) ?? preset.defaultDensity;
  const register = copyRegisterFor(personality);
  const ctaDefault =
    (register === 'formal' ? FORMAL_REGISTER[bucket]?.ctaLabel : undefined) ?? CTAS[bucket];
  return {
    vibe: preset.vibe,
    layoutShell: preset.shell,
    sectionOrder: [...SECTIONS[bucket]],
    ctaDefault,
    label: `${preset.name} · ${DENSITY_LABELS[resolvedDensity]}`,
    density: resolvedDensity,
    bucket,
    presetId: preset.id,
    presetName: preset.name,
    characters: characterChips(personality),
  };
}

export function nextLookVariant(current: number, dir: 1 | -1, _personality: readonly Personality[] = [], industry?: string): number {
  const n = PRESETS[industryBucket(industry)].length || 1;
  return (((current + dir) % n) + n) % n;
}

/**
 * Seed the starting preset from the business name so two neighboring
 * referrals in the same industry do not open on identical looks.
 */
export function seedLookVariant(businessName: string | undefined, industry?: string): number {
  const name = (businessName ?? '').trim().toLowerCase();
  if (!name) return 0;
  let h = 0;
  for (let i = 0; i < name.length; i += 1) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  const n = PRESETS[industryBucket(industry)].length || 1;
  return h % n;
}

function interpolate(template: string, name: string): string {
  return template.replace(/\{name\}/g, name);
}

function packFor(
  bucket: IndustryBucket,
  name: string,
  emailLocal: string,
  register: CopyRegister = 'standard',
): PlaceholderPack {
  const packs: Record<IndustryBucket, PlaceholderPack> = {
    'insurance-adjusting': {
      headline: "Don't settle for less than you deserve.",
      tagline: 'Licensed public adjusters on your side',
      valueProp: `${name} is a licensed public adjusting practice. We work for you — not the insurance company — and we fight for a full, fair settlement.`,
      ctaLabel: 'Free claim review',
      phone: '(555) 201-0140',
      email: `hello@${emailLocal}.com`,
      serviceArea: 'Serving Oklahoma and surrounding storm regions',
      proofChips: ['Licensed public adjusters', 'Storm damage experts', 'Family-owned'],
      services: [
        {
          title: 'Residential claims',
          description: 'Roof, hail, wind, and water claims handled from inspection through settlement.',
          priceLabel: 'No upfront fee',
        },
        {
          title: 'Commercial claims',
          description: 'Business interruption and building damage with a documented, defensible estimate.',
        },
        {
          title: 'Storm response',
          description: 'On-site after the weather clears. We document the loss before the carrier does.',
        },
      ],
      faqs: [
        {
          q: 'What does a public adjuster do?',
          a: 'We represent you, not the insurance company. We inspect, document, and negotiate so you are not leaving money on the table.',
        },
        {
          q: 'Do you charge anything up front?',
          a: 'No. We work on a contingency — if we do not improve the outcome, you do not pay us.',
        },
        {
          q: `Where does ${name} work?`,
          a: 'We handle claims across the state and travel for major storm events. Call to confirm your area.',
        },
        {
          q: 'How fast can you start?',
          a: 'Most inspections are scheduled within 48 hours. After a named storm we staff extra crews.',
        },
      ],
      testimonials: [
        {
          quote: 'They found damage the carrier missed and the supplement more than paid for itself.',
          name: 'Jordan Hale',
          role: 'Homeowner',
          initials: 'JH',
        },
        {
          quote: 'Clear communication the whole way. I finally understood my policy.',
          name: 'Priya Nair',
          role: 'Business owner',
          initials: 'PN',
        },
        {
          quote: 'After the hail, they were on the roof the next morning.',
          name: 'Marcus Webb',
          role: 'Verified client',
          initials: 'MW',
        },
      ],
      valueProps: [
        { icon: 'shield', title: 'Licensed for you', blurb: 'Public adjusters work for the policyholder. That is the whole point.' },
        { icon: 'bolt', title: 'Storm-ready', blurb: 'We document losses quickly so the file is complete before the first offer.' },
        { icon: 'medal', title: 'Settlement-first', blurb: 'Estimates built to survive desk review, not to look pretty in a slide deck.' },
      ],
      pricingTiers: [
        {
          name: 'Claim review',
          priceLabel: 'Free',
          description: 'We look at the carrier offer and tell you, honestly, if we can help.',
          features: ['Policy review', 'Damage photos reviewed', 'No obligation'],
          ctaLabel: 'Free claim review',
        },
        {
          name: 'Full representation',
          priceLabel: 'Contingency',
          description: 'Inspection, estimate, and negotiation until the claim is closed.',
          features: ['On-site inspection', 'Line-item estimate', 'Carrier negotiation', 'Supplement support'],
          ctaLabel: 'Start a claim',
          highlight: true,
        },
        {
          name: 'Commercial',
          priceLabel: 'Custom',
          description: 'Larger buildings and business interruption with a dedicated file lead.',
          features: ['Multi-building scope', 'BI documentation', 'Weekly status'],
          ctaLabel: 'Talk to us',
        },
      ],
      reviewBadges: [
        { platform: 'Google', score: 4.9, count: '80+', label: '4.9 on Google' },
        { platform: 'BBB', score: 5, count: 'A+', label: 'BBB A+' },
      ],
      trustRow: { rating: '4.9', reviews: '80+ reviews', years: '20+ years', jobs: 'Family-owned' },
    },
    trades: {
      headline: 'Show up. Fix it. Leave it better.',
      tagline: 'Local, licensed, and easy to reach',
      valueProp: `${name} handles the work right the first time — clear quotes, clean job sites, and a number you can actually call.`,
      ctaLabel: 'Call now',
      phone: '(555) 201-0110',
      email: `hello@${emailLocal}.com`,
      serviceArea: 'Serving the greater metro area',
      proofChips: ['Licensed & insured', 'Same-week scheduling', 'Local crew'],
      services: [
        { title: 'Service call', description: 'Diagnose the issue, explain the fix, and give you a written price before we start.', priceLabel: 'From $129' },
        { title: 'Repair & install', description: 'From a single fixture to a full replacement, scheduled around your week.' },
        { title: 'Maintenance plan', description: 'Seasonal checkups so the next emergency is not a surprise.' },
      ],
      faqs: [
        { q: 'Do you offer free estimates?', a: 'Yes. Standard estimates are free. Emergency after-hours visits have a posted trip fee.' },
        { q: 'Are you licensed and insured?', a: 'Yes. We carry liability coverage and pull permits when the job requires it.' },
        { q: 'How soon can you come out?', a: 'Most non-emergency jobs land within a week. Same-day slots open when a crew finishes early.' },
        { q: 'What forms of payment do you take?', a: 'Card, check, and ACH. A deposit holds the appointment on larger installs.' },
      ],
      testimonials: [
        { quote: 'On time, fair price, and they cleaned up. I already booked them again.', name: 'Chris Molina', role: 'Homeowner', initials: 'CM' },
        { quote: 'Explained the options without the upsell routine. Refreshing.', name: 'Dana Liu', role: 'Property manager', initials: 'DL' },
        { quote: 'Fixed what two other companies guessed at.', name: 'Evan Brooks', role: 'Verified customer', initials: 'EB' },
      ],
      valueProps: [
        { icon: 'clock', title: 'On the calendar', blurb: 'Real arrival windows, not “sometime Thursday.”' },
        { icon: 'shield', title: 'Licensed & insured', blurb: 'Coverage and permits handled so you are not chasing paperwork.' },
        { icon: 'medal', title: 'Workmanship guarantee', blurb: 'If it is not right, we come back and make it right.' },
      ],
      pricingTiers: [
        { name: 'Service', priceLabel: 'From $129', description: 'Trip, diagnosis, and a written fix.', features: ['Written quote', 'Standard hours'], ctaLabel: 'Book a visit' },
        { name: 'Project', priceLabel: 'Quoted', description: 'Larger installs with a fixed price before we start.', features: ['Fixed bid', 'Permit handling', 'Cleanup included'], ctaLabel: 'Get a quote', highlight: true },
        { name: 'Plan', priceLabel: 'Annual', description: 'Two visits a year and priority scheduling.', features: ['Priority slots', 'Member pricing'], ctaLabel: 'Ask about plans' },
      ],
      reviewBadges: [
        { platform: 'Google', score: 4.9, count: '200+', label: '4.9 on Google' },
        { platform: 'Angi', score: 5, count: 'Top Pro', label: 'Angi Super Service' },
      ],
      trustRow: { rating: '4.9', reviews: '200+ reviews', years: '10+ years', jobs: '1,200+ jobs' },
    },
    professional: {
      headline: 'Clear thinking. Clean delivery.',
      tagline: 'Strategy that ships',
      valueProp: `${name} helps teams make the next decision with a plan they can actually run — not a 60-page deck that dies in a folder.`,
      ctaLabel: 'Book a consult',
      phone: '(555) 201-0120',
      email: `hello@${emailLocal}.com`,
      serviceArea: 'Remote-first, on-site by arrangement',
      proofChips: ['Senior-led', 'Fixed scopes', 'No bait-and-switch'],
      services: [
        { title: 'Discovery sprint', description: 'Two weeks to map the problem, the stakeholders, and the first shippable slice.' },
        { title: 'Retainer', description: 'A named lead who already knows the context when you ping them.' },
        { title: 'Build partnership', description: 'We stay through launch and the first month of real users.' },
      ],
      faqs: [
        { q: 'How do engagements start?', a: 'A 30-minute call, then a short written proposal. No 12-slide pitch theater.' },
        { q: 'Do you work with startups or incumbents?', a: 'Both. The fit is whether you want a partner who will tell you the uncomfortable part early.' },
        { q: 'Where are you based?', a: 'We work remotely across US time zones and travel for workshops when it is worth it.' },
      ],
      testimonials: [
        { quote: 'They cut a six-month roadmap down to the three things that mattered.', name: 'Alicia Grant', role: 'COO', initials: 'AG' },
        { quote: 'The only consultants who left us with a working process, not a PDF.', name: 'Noah Patel', role: 'Founder', initials: 'NP' },
      ],
      valueProps: [
        { icon: 'sparkle', title: 'Senior in the room', blurb: 'You are not paying for a deck built by someone you will never meet.' },
        { icon: 'bolt', title: 'Shipped, not theorized', blurb: 'We measure the engagement by what is live, not what is recommended.' },
        { icon: 'shield', title: 'Fixed scopes', blurb: 'Change control in writing. No surprise hourly bleed.' },
      ],
      pricingTiers: [
        { name: 'Sprint', priceLabel: 'Fixed', description: 'A contained two-week discovery.', features: ['Kickoff', 'Written findings', 'Next-step plan'], ctaLabel: 'Book a sprint' },
        { name: 'Retainer', priceLabel: 'Monthly', description: 'Ongoing access to a named lead.', features: ['Weekly working session', 'Slack access'], ctaLabel: 'Talk retainer', highlight: true },
        { name: 'Build', priceLabel: 'Quoted', description: 'Delivery through launch.', features: ['Dedicated pod', 'Launch support'], ctaLabel: 'Request a quote' },
      ],
      reviewBadges: [{ platform: 'Clutch', score: 5, count: '12', label: '5.0 on Clutch' }],
      trustRow: { rating: '5.0', reviews: 'Client references', years: '8+ years', jobs: 'Senior-led' },
    },
    wellness: {
      headline: 'Care that actually feels like care.',
      tagline: 'Calm rooms. Clear plans.',
      valueProp: `${name} is a place to land — unhurried appointments, honest recommendations, and a team that remembers your name.`,
      ctaLabel: 'Book now',
      phone: '(555) 201-0130',
      email: `hello@${emailLocal}.com`,
      serviceArea: 'Neighborhood studio — easy parking',
      proofChips: ['Licensed practitioners', 'New-client welcome', 'Quiet rooms'],
      services: [
        { title: 'Signature session', description: 'The visit most first-timers book. We start with a conversation, not a script.' },
        { title: 'Membership', description: 'A monthly rhythm so you do not have to remember to take care of yourself.' },
        { title: 'Packages', description: 'A focused series for a specific goal — skin, recovery, or mobility.' },
      ],
      faqs: [
        { q: 'How do I book?', a: 'Use the form or call. New clients get a short intake so the first visit is not wasted on paperwork.' },
        { q: 'What should I expect on the first visit?', a: 'Arrive a few minutes early. We will talk through goals, then treat — no hard sell on extras.' },
        { q: 'Do you take insurance?', a: 'Some plans, not all. We will tell you before you book if we can bill yours.' },
      ],
      testimonials: [
        { quote: 'The first place that did not rush me out the door.', name: 'Maya Chen', role: 'Client', initials: 'MC' },
        { quote: 'I actually look forward to the appointment. That is new.', name: 'Sam Ortiz', role: 'Member', initials: 'SO' },
      ],
      valueProps: [
        { icon: 'leaf', title: 'Unhurried', blurb: 'The clock is not the product. The session is.' },
        { icon: 'shield', title: 'Licensed care', blurb: 'Credentials on the wall, and we will explain anything you ask.' },
        { icon: 'sparkle', title: 'A room that calms', blurb: 'Lighting, sound, and scent considered — not an afterthought.' },
      ],
      pricingTiers: [
        { name: 'Visit', priceLabel: 'From $95', description: 'A single session with intake.', features: ['Consultation', 'Treatment'], ctaLabel: 'Book a visit' },
        { name: 'Member', priceLabel: 'Monthly', description: 'Two visits and member pricing on extras.', features: ['Priority booking', 'Guest pass'], ctaLabel: 'Join', highlight: true },
        { name: 'Series', priceLabel: 'Package', description: 'Four or six visits aimed at one goal.', features: ['Written plan', 'Progress check'], ctaLabel: 'Ask about series' },
      ],
      reviewBadges: [{ platform: 'Google', score: 5, count: '120+', label: '5.0 on Google' }],
      trustRow: { rating: '5.0', reviews: '120+ reviews', years: 'Established', jobs: 'Licensed team' },
    },
    hospitality: {
      headline: 'A table worth leaving the house for.',
      tagline: 'Seasonal plates. Warm room.',
      valueProp: `${name} is the neighborhood table — a short menu done well, a room you want to linger in, and a staff that still looks you in the eye.`,
      ctaLabel: 'Reserve a table',
      phone: '(555) 201-0150',
      email: `hello@${emailLocal}.com`,
      serviceArea: 'Downtown — walk-ins welcome when we have a seat',
      proofChips: ['Seasonal menu', 'Walk-ins welcome', 'Private dining'],
      services: [
        { title: 'Dinner', description: 'A focused menu that changes with the market, not with a corporate calendar.' },
        { title: 'Private dining', description: 'The back room for birthdays, closes, and the nights that need a door.' },
        { title: 'Catering', description: 'The same kitchen, at your place. Minimums are honest and posted.' },
      ],
      faqs: [
        { q: 'Do you take reservations?', a: 'Yes, for dinner. Lunch is mostly walk-in. Large parties should call.' },
        { q: 'Is there a vegetarian menu?', a: 'Always at least two plates that are not an afterthought. Ask and we will steer you.' },
        { q: 'Where do we park?', a: 'Street parking after 6, plus a lot one block west. We will text a pin when you book.' },
      ],
      testimonials: [
        { quote: 'The kind of place you immediately add to the “when parents visit” list.', name: 'Riley James', role: 'Guest', initials: 'RJ' },
        { quote: 'Private room for eight, no fuss, food that actually arrived hot.', name: 'Hannah Cole', role: 'Dinner host', initials: 'HC' },
      ],
      valueProps: [
        { icon: 'leaf', title: 'Seasonal', blurb: 'The menu moves when the market does.' },
        { icon: 'sparkle', title: 'A room with a pulse', blurb: 'Music, light, and enough space between tables to talk.' },
        { icon: 'medal', title: 'Hospitality, not theater', blurb: 'We remember the regulars and we do not rush the check.' },
      ],
      pricingTiers: [
        { name: 'Table', priceLabel: 'À la carte', description: 'Walk in or reserve.', features: ['Full menu', 'Bar'], ctaLabel: 'Reserve' },
        { name: 'Private', priceLabel: 'Quoted', description: 'The back room, set menu optional.', features: ['Seated 8–16', 'Custom cake OK'], ctaLabel: 'Plan an event', highlight: true },
        { name: 'Catering', priceLabel: 'Per head', description: 'Drop-off or staffed.', features: ['Minimums posted', 'Reheats included'], ctaLabel: 'Ask about catering' },
      ],
      reviewBadges: [{ platform: 'Google', score: 4.8, count: '300+', label: '4.8 on Google' }],
      trustRow: { rating: '4.8', reviews: '300+ reviews', years: 'Neighborhood staple', jobs: 'Open nightly' },
    },
    'legal-finance': {
      headline: 'Advice you can act on this week.',
      tagline: 'Clear counsel. No theatrics.',
      valueProp: `${name} is a practice people call when the stakes are real — documents that hold up, timelines that are honest, and a partner who answers the phone.`,
      ctaLabel: 'Schedule a consult',
      phone: '(555) 201-0160',
      email: `hello@${emailLocal}.com`,
      serviceArea: 'Statewide practice · virtual consults available',
      proofChips: ['Licensed', 'Confidential', 'Direct attorney access'],
      services: [
        { title: 'Consultation', description: 'A focused first meeting so you know where you stand before you spend.' },
        { title: 'Representation', description: 'We take the file, set a plan, and you get a named attorney — not a rotating inbox.' },
        { title: 'Ongoing counsel', description: 'A retainer for businesses that need a call answered before it becomes a matter.' },
      ],
      faqs: [
        { q: 'Is the first consult billed?', a: 'The first 30 minutes are complimentary for new matters. After that we quote in writing.' },
        { q: 'Do you take cases outside the city?', a: 'Yes. Virtual consults statewide; court appearances by arrangement.' },
        { q: 'How do retainers work?', a: 'A written agreement, a trust deposit, and monthly statements. No surprise billing codes.' },
      ],
      testimonials: [
        { quote: 'They explained the risk in English and then did exactly what they said.', name: 'Gwen Hart', role: 'Client', initials: 'GH' },
        { quote: 'Responsive without being theatrical. That is rare.', name: 'Luis Ortega', role: 'Business owner', initials: 'LO' },
      ],
      valueProps: [
        { icon: 'shield', title: 'Confidential by default', blurb: 'What you tell us stays in the file.' },
        { icon: 'medal', title: 'Named counsel', blurb: 'You will know who is working the matter.' },
        { icon: 'clock', title: 'Timelines we keep', blurb: 'If a date slips, you hear it from us first.' },
      ],
      pricingTiers: [
        { name: 'Consult', priceLabel: 'Complimentary', description: 'Thirty minutes to map the matter.', features: ['Conflict check', 'Next-step memo'], ctaLabel: 'Schedule' },
        { name: 'Matter', priceLabel: 'Quoted', description: 'A defined engagement letter.', features: ['Named attorney', 'Written scope'], ctaLabel: 'Request a quote', highlight: true },
        { name: 'Counsel', priceLabel: 'Retainer', description: 'Monthly access for ongoing needs.', features: ['Priority call-back', 'Document review'], ctaLabel: 'Ask about counsel' },
      ],
      reviewBadges: [{ platform: 'Google', score: 5, count: '40+', label: '5.0 on Google' }],
      trustRow: { rating: '5.0', reviews: '40+ reviews', years: 'Established practice', jobs: 'Licensed' },
    },
    creative: {
      headline: 'Work that still looks like you in ten years.',
      tagline: 'Photography & film, considered',
      valueProp: `${name} photographs people and places with enough patience that the pictures still feel true a decade later.`,
      ctaLabel: 'Check availability',
      phone: '(555) 201-0170',
      email: `hello@${emailLocal}.com`,
      serviceArea: 'Travel welcome · based locally',
      proofChips: ['Full-day coverage', 'Film + digital', 'Prints included'],
      services: [
        { title: 'Wedding day', description: 'Coverage that does not make you perform for the camera.' },
        { title: 'Editorial / brand', description: 'A day with your team, your space, and a shot list we actually finish.' },
        { title: 'Portraits', description: 'Studio or location. A small set you will still like next year.' },
      ],
      faqs: [
        { q: 'How far ahead should we book?', a: 'Peak dates go 9–12 months out. Weekdays and off-season are more flexible.' },
        { q: 'Do you travel?', a: 'Yes. Travel fees are quoted up front — no surprise day-of mileage.' },
        { q: 'When do we see photos?', a: 'A handful of images within a week; the full gallery on the timeline in your contract.' },
      ],
      testimonials: [
        { quote: 'We forgot the camera was there, which is exactly what we wanted.', name: 'Elena & Drew', role: 'Wedding clients', initials: 'ED' },
        { quote: 'The brand day did not feel like a production. It felt like us, working.', name: 'Ivy Park', role: 'Studio owner', initials: 'IP' },
      ],
      valueProps: [
        { icon: 'sparkle', title: 'Unposed on purpose', blurb: 'Direction when you need it, space when you do not.' },
        { icon: 'clock', title: 'A timeline we keep', blurb: 'Galleries land when the contract says they will.' },
        { icon: 'medal', title: 'Prints that last', blurb: 'Archival files and a print credit so they leave the hard drive.' },
      ],
      pricingTiers: [
        { name: 'Portrait', priceLabel: 'From $450', description: 'A focused set, location or studio.', features: ['1 hour', 'Print credit'], ctaLabel: 'Inquire' },
        { name: 'Day', priceLabel: 'Quoted', description: 'Wedding or brand coverage.', features: ['Second shooter option', 'Full gallery'], ctaLabel: 'Check dates', highlight: true },
        { name: 'Film add-on', priceLabel: 'Add-on', description: 'Super 8 or digital film for the same day.', features: ['Highlight edit'], ctaLabel: 'Ask about film' },
      ],
      reviewBadges: [{ platform: 'Google', score: 5, count: '60+', label: '5.0 on Google' }],
      trustRow: { rating: '5.0', reviews: '60+ reviews', years: 'Working artist', jobs: 'Travel welcome' },
    },
    default: {
      headline: interpolate('The obvious next call is {name}.', name),
      tagline: 'Straightforward. Local. Easy to reach.',
      valueProp: `${name} is built around a simple promise: tell people who you are, what you do, and how to reach you — then make that true.`,
      ctaLabel: 'Get in touch',
      phone: '(555) 201-0100',
      email: `hello@${emailLocal}.com`,
      serviceArea: 'Serving the greater metro area',
      proofChips: ['Local team', 'Clear pricing', 'Easy to reach'],
      services: [
        { title: 'Primary offering', description: `The work ${name} is known for — scoped clearly, priced in writing.` },
        { title: 'Consultation', description: 'A first conversation so you know whether we are the right fit.' },
        { title: 'Follow-through', description: 'We stay on the job until you are satisfied, not until the invoice clears.' },
      ],
      faqs: [
        { q: `How do I start with ${name}?`, a: 'Call, email, or use the form. We reply within one business day.' },
        { q: 'Do you quote in writing?', a: 'Yes. No work starts until you have a number you agreed to.' },
        { q: 'What area do you serve?', a: 'The greater metro, with travel quoted for anything farther.' },
      ],
      testimonials: [
        { quote: 'Easy to work with and they did what they said they would.', name: 'Taylor Reeves', role: 'Client', initials: 'TR' },
        { quote: 'The site made it obvious who to call. Then they picked up.', name: 'Morgan Adeyemi', role: 'New customer', initials: 'MA' },
      ],
      valueProps: [
        { icon: 'bolt', title: 'Easy to reach', blurb: 'A real phone number and a form that goes somewhere.' },
        { icon: 'shield', title: 'Straight answers', blurb: 'If we cannot help, we will say so quickly.' },
        { icon: 'medal', title: 'Local reputation', blurb: 'We live here. The work has to hold up at the grocery store.' },
      ],
      pricingTiers: [
        { name: 'Start', priceLabel: 'Quoted', description: 'A first project with a written scope.', features: ['Kickoff call', 'Fixed quote'], ctaLabel: 'Get in touch' },
        { name: 'Standard', priceLabel: 'Most booked', description: 'The engagement most clients choose.', features: ['Named contact', 'Weekly updates'], ctaLabel: 'Ask about this', highlight: true },
        { name: 'Ongoing', priceLabel: 'Retainer', description: 'Stay in the mix after launch.', features: ['Priority support'], ctaLabel: 'Talk retainer' },
      ],
      reviewBadges: [{ platform: 'Google', score: 4.9, count: '50+', label: '4.9 on Google' }],
      trustRow: { rating: '4.9', reviews: '50+ reviews', years: 'Local', jobs: 'Owner-operated' },
    },
  };

  const base = packs[bucket];
  const override = register === 'formal' ? FORMAL_REGISTER[bucket] : undefined;
  const pack = override ? { ...base, ...override } : base;
  return {
    ...pack,
    headline: interpolate(pack.headline, name),
    tagline: interpolate(pack.tagline, name),
    valueProp: interpolate(pack.valueProp, name),
    email: pack.email,
    services: pack.services.map((s) => ({
      ...s,
      title: interpolate(s.title ?? '', name),
      description: interpolate(s.description ?? '', name),
    })),
    faqs: pack.faqs.map((f) => ({ q: interpolate(f.q, name), a: interpolate(f.a, name) })),
    testimonials: pack.testimonials.map((t) => ({ ...t, quote: interpolate(t.quote, name) })),
    valueProps: pack.valueProps.map((v) => ({
      ...v,
      title: interpolate(v.title, name),
      blurb: interpolate(v.blurb, name),
    })),
  };
}

export function placeholderPackFor(
  industry: string | undefined,
  businessName: string,
  register: CopyRegister = 'standard',
): PlaceholderPack {
  const name = businessName.trim() || 'Your Business';
  return packFor(industryBucket(industry), name, emailSlugFromName(name), register);
}

export type PlaceholderCopyMode = 'none' | 'empty' | 'replaceDemo';

export type PlaceholderOverlayOpts = {
  /**
   * `none` — look/layout only, do not write pack copy.
   * `empty` — fill blank fields (Generate).
   * `replaceDemo` — fill blank or pack-matching fields; never customer-owned (Fill demo).
   */
  copy?: PlaceholderCopyMode;
  /** @deprecated Prefer `copy: 'replaceDemo'`. */
  replaceDemo?: boolean;
  previousIndustry?: string;
  /** Copy voice for headline/tagline/CTA; derived from character chips. */
  register?: CopyRegister;
};

export function resolveCopyMode(opts?: PlaceholderOverlayOpts): PlaceholderCopyMode {
  if (opts?.copy) return opts.copy;
  if (opts?.replaceDemo) return 'replaceDemo';
  return 'empty';
}

function blank(s: string | undefined | null): boolean {
  return !s || !String(s).trim();
}

function sameText(a: string | undefined | null, b: string | undefined | null): boolean {
  return (a ?? '').trim() === (b ?? '').trim();
}

function replaceableString(
  current: string | undefined,
  packValue: string,
  previousValues: ReadonlyArray<string | undefined>,
  replaceDemo: boolean,
): boolean {
  if (blank(current)) return true;
  if (!replaceDemo) return false;
  if (sameText(current, packValue)) return true;
  return previousValues.some((v) => !blank(v) && sameText(current, v));
}

function pickString(
  current: string | undefined,
  packValue: string,
  previousValues: ReadonlyArray<string | undefined>,
  replaceDemo: boolean,
): string {
  return replaceableString(current, packValue, previousValues, replaceDemo) ? packValue : (current as string);
}

function serviceTitleSet(services: Service[]): Set<string> {
  return new Set(
    services
      .map((s) => (s.title ?? '').trim().toLowerCase())
      .filter(Boolean),
  );
}

function mergeServices(
  existing: Service[] | undefined,
  pack: Service[],
  previousPack?: Service[],
  replaceDemo?: boolean,
): Service[] {
  if (!replaceDemo) {
    const real = (existing ?? []).filter((s) => s.title?.trim());
    if (real.length === 0) return pack;
    const out: Service[] = real.map((s, i) => ({
      title: s.title,
      description: s.description?.trim() ? s.description : pack[i]?.description ?? s.description,
      priceLabel: s.priceLabel?.trim() ? s.priceLabel : pack[i]?.priceLabel,
    }));
    if (out.length >= 3) return out;
    const used = new Set(out.map((s) => s.title?.toLowerCase()));
    for (const extra of pack) {
      if (out.length >= 3) break;
      if (used.has((extra.title ?? '').toLowerCase())) continue;
      out.push(extra);
    }
    return out;
  }

  const demoTitles = new Set([...serviceTitleSet(pack), ...serviceTitleSet(previousPack ?? [])]);
  const out: Service[] = (existing ?? []).filter((s) => {
    const title = (s.title ?? '').trim();
    return title && !demoTitles.has(title.toLowerCase());
  });
  const used = new Set(out.map((s) => (s.title ?? '').trim().toLowerCase()));
  for (const extra of pack) {
    if (out.length >= 3) break;
    const title = (extra.title ?? '').trim().toLowerCase();
    if (!title || used.has(title)) continue;
    out.push(extra);
    used.add(title);
  }
  return out.length > 0 ? out : pack;
}

function mergeProofChips(
  existing: string[] | undefined,
  pack: string[],
  previousPack: string[] | undefined,
  replaceDemo: boolean,
): string[] {
  const hasAny = existing?.some((c) => c.trim());
  if (!replaceDemo) return hasAny ? (existing as string[]) : pack;
  if (!hasAny) return pack;
  if (JSON.stringify(existing) === JSON.stringify(pack)) return pack;
  if (previousPack && JSON.stringify(existing) === JSON.stringify(previousPack)) return pack;
  return existing as string[];
}

function mergeTestimonials(
  existing: TestimonialItem[] | undefined,
  pack: TestimonialItem[],
  previousPack: TestimonialItem[] | undefined,
  replaceDemo: boolean,
): TestimonialItem[] {
  if (!existing?.length) return pack;
  if (!replaceDemo) return existing;
  const demoQuotes = new Set(
    [...pack, ...(previousPack ?? [])].map((t) => t.quote.trim()).filter(Boolean),
  );
  const first = existing[0]?.quote?.trim();
  if (first && !demoQuotes.has(first)) return existing;
  return pack;
}

/** Overlay industry placeholders onto empty DNA fields. Typed values always win. */
export function applyPlaceholderPackToBusiness(
  info: BusinessInfo,
  industry: string | undefined,
  opts?: PlaceholderOverlayOpts,
): BusinessInfo {
  const copy = resolveCopyMode(opts);
  if (copy === 'none') {
    return {
      ...info,
      industryVertical: info.industryVertical?.trim() || industry?.trim() || info.industryVertical,
    };
  }

  const name = info.name?.trim() || 'Your Business';
  const nextIndustry = industry ?? info.industryVertical;
  const register = opts?.register ?? 'standard';
  const pack = placeholderPackFor(nextIndustry, name, register);
  const replaceDemo = copy === 'replaceDemo';
  const previousPack =
    replaceDemo && opts?.previousIndustry && opts.previousIndustry !== nextIndustry
      ? placeholderPackFor(opts.previousIndustry, name, register)
      : undefined;
  // Copy in the *other* register still counts as demo copy, so toggling
  // Formal/Heritage lets Fill demo re-voice the headline/tagline/CTA.
  const altPack = replaceDemo
    ? placeholderPackFor(nextIndustry, name, register === 'formal' ? 'standard' : 'formal')
    : undefined;

  return {
    ...info,
    name,
    industryVertical: info.industryVertical?.trim() || industry?.trim() || info.industryVertical,
    headline: replaceDemo
      ? pickString(info.headline, pack.headline, [previousPack?.headline, altPack?.headline], true)
      : blank(info.headline)
        ? (!blank(info.valueProp) ? info.valueProp : pack.headline)
        : info.headline,
    tagline: pickString(info.tagline, pack.tagline, [previousPack?.tagline, altPack?.tagline], replaceDemo),
    valueProp: pickString(info.valueProp, pack.valueProp, [previousPack?.valueProp], replaceDemo),
    ctaLabel: pickString(info.ctaLabel, pack.ctaLabel, [previousPack?.ctaLabel, altPack?.ctaLabel], replaceDemo),
    phone: pickString(info.phone, pack.phone, [previousPack?.phone], replaceDemo),
    email: pickString(info.email, pack.email, [previousPack?.email], replaceDemo),
    serviceArea: pickString(info.serviceArea, pack.serviceArea, [previousPack?.serviceArea], replaceDemo),
    proofChips: mergeProofChips(info.proofChips, pack.proofChips, previousPack?.proofChips, replaceDemo),
    services: mergeServices(info.services, pack.services, previousPack?.services, replaceDemo),
    faqs: info.faqs && info.faqs.length > 0 ? info.faqs : pack.faqs,
    testimonials: mergeTestimonials(
      info.testimonials,
      pack.testimonials,
      previousPack?.testimonials,
      replaceDemo,
    ),
    valueProps: info.valueProps && info.valueProps.length > 0 ? info.valueProps : pack.valueProps,
    pricingTiers: info.pricingTiers && info.pricingTiers.length > 0 ? info.pricingTiers : pack.pricingTiers,
    reviewBadges: info.reviewBadges && info.reviewBadges.length > 0 ? info.reviewBadges : pack.reviewBadges,
    trustRow: info.trustRow?.rating || info.trustRow?.reviews ? info.trustRow : pack.trustRow,
  };
}

export function applyLookToSiteDna(
  dna: SiteDNA,
  industry: string | undefined,
  opts?: { forceSections?: boolean } & PlaceholderOverlayOpts,
): SiteDNA {
  const personality = dna.designTokens.personality ?? [];
  const look = resolveLook(
    industry ?? dna.businessInfo.industryVertical,
    personality,
    dna.designTokens.lookVariant ?? 0,
    dna.designTokens.density,
  );
  const copy = resolveCopyMode(opts);
  const seededCta =
    copy === 'none'
      ? dna.businessInfo
      : {
          ...dna.businessInfo,
          ctaLabel: blank(dna.businessInfo.ctaLabel) ? look.ctaDefault : dna.businessInfo.ctaLabel,
        };
  const businessInfo = applyPlaceholderPackToBusiness(seededCta, industry ?? dna.businessInfo.industryVertical, {
    ...opts,
    copy,
    register: opts?.register ?? copyRegisterFor(personality),
  });
  const nextSections = opts?.forceSections || dna.layout.sectionOrder.length === 0 ? look.sectionOrder : dna.layout.sectionOrder;
  const pageCount = dna.layout.pageCount;
  const packaged =
    opts?.forceSections && pageCount
      ? applyPackageToLayout(look.sectionOrder, pageCount)
      : pageCount && (dna.layout.pages?.length ?? 0) === 0
        ? applyPackageToLayout(nextSections, pageCount)
        : null;
  const prevAccent = dna.designTokens.multiTonePalette?.accent;
  const palette = generateMultiTonePalette(look.vibe);
  return {
    ...dna,
    businessInfo,
    designTokens: {
      ...dna.designTokens,
      vibe: look.vibe,
      layoutShell: look.layoutShell,
      multiTonePalette: prevAccent ? { ...palette, accent: prevAccent } : palette,
      typography: typographyForVibe(look.vibe),
    },
    layout: packaged
      ? { ...dna.layout, ...packaged }
      : {
          ...dna.layout,
          sectionOrder: nextSections,
        },
  };
}

export function applyPlaceholderPack(lead: LeadRecord): LeadRecord {
  const industry = lead.context.industryVertical || lead.siteDna.businessInfo.industryVertical;
  return {
    ...lead,
    siteDna: {
      ...lead.siteDna,
      businessInfo: applyPlaceholderPackToBusiness(lead.siteDna.businessInfo, industry),
    },
  };
}

/**
 * Refresh look + placeholders. Used on create, industry/personality change,
 * Fill demo content, and look buttons. Pass `forceSections` to replace section order.
 */
export function applyCallDefaults(
  lead: LeadRecord,
  opts?: { forceSections?: boolean } & PlaceholderOverlayOpts,
): LeadRecord {
  const industry = lead.context.industryVertical || lead.siteDna.businessInfo.industryVertical;
  const siteDna = applyLookToSiteDna(lead.siteDna, industry, {
    forceSections: opts?.forceSections ?? true,
    copy: resolveCopyMode(opts),
    previousIndustry: opts?.previousIndustry,
  });
  return {
    ...lead,
    context: {
      ...lead.context,
      industryVertical: industry ?? lead.context.industryVertical,
    },
    siteDna: {
      ...siteDna,
      businessInfo: {
        ...siteDna.businessInfo,
        industryVertical: industry ?? siteDna.businessInfo.industryVertical,
      },
    },
  };
}

/** Fields currently equal to the industry pack (for Demo fill hints on the sheet). */
export function demoFilledFields(info: BusinessInfo, industry: string | undefined): Set<string> {
  const name = info.name ?? 'Your Business';
  const pack = placeholderPackFor(industry ?? info.industryVertical, name);
  // Copy from either register is still demo copy.
  const formalPack = placeholderPackFor(industry ?? info.industryVertical, name, 'formal');
  const marked = new Set<string>();
  const check = (key: string, a: string | undefined, ...values: string[]) => {
    if (!blank(a) && values.some((b) => sameText(a, b))) marked.add(key);
  };
  check('headline', info.headline, pack.headline, formalPack.headline);
  check('tagline', info.tagline, pack.tagline, formalPack.tagline);
  check('valueProp', info.valueProp, pack.valueProp);
  check('ctaLabel', info.ctaLabel, pack.ctaLabel, formalPack.ctaLabel);
  check('phone', info.phone, pack.phone);
  check('email', info.email, pack.email);
  check('serviceArea', info.serviceArea, pack.serviceArea);
  if ((info.proofChips ?? []).some((c) => c.trim()) && JSON.stringify(info.proofChips ?? []) === JSON.stringify(pack.proofChips)) {
    marked.add('proofChips');
  }
  const customTitles = customerServiceTitles(info, industry);
  const titled = (info.services ?? []).filter((s) => s.title?.trim());
  if (titled.length > 0 && customTitles.length === 0) marked.add('services');
  const quote = info.testimonials?.[0]?.quote;
  if (!blank(quote) && sameText(quote, pack.testimonials[0]?.quote)) marked.add('testimonial');
  return marked;
}

/** Service titles that are not from the current industry pack. */
export function customerServiceTitles(info: BusinessInfo, industry: string | undefined): string[] {
  const pack = placeholderPackFor(industry ?? info.industryVertical, info.name ?? 'Your Business');
  const demoTitles = serviceTitleSet(pack.services);
  return (info.services ?? [])
    .map((s) => (s.title ?? '').trim())
    .filter((title) => title && !demoTitles.has(title.toLowerCase()));
}

export type CallSheetGapKind = 'name' | 'industry' | 'area' | 'headline' | 'services' | 'contact';

export interface CallSheetGap {
  kind: CallSheetGapKind;
  label: string;
}

/** Print-critical gaps for the sticky bar. Empty array means ready to spring. */
export function callSheetPrintGaps(info: BusinessInfo, industry: string | undefined): CallSheetGap[] {
  const demo = demoFilledFields(info, industry);
  const gaps: CallSheetGap[] = [];
  if (blank(info.name) || info.name?.trim() === 'Your Business') gaps.push({ kind: 'name', label: 'Name empty' });
  if (blank(industry ?? info.industryVertical)) gaps.push({ kind: 'industry', label: 'Industry empty' });
  if (blank(info.serviceArea)) gaps.push({ kind: 'area', label: 'Area empty' });
  else if (demo.has('serviceArea')) gaps.push({ kind: 'area', label: 'Area still demo' });
  if (blank(info.headline)) gaps.push({ kind: 'headline', label: 'Headline empty' });
  else if (demo.has('headline')) gaps.push({ kind: 'headline', label: 'Headline still demo' });
  const custom = customerServiceTitles(info, industry);
  if (custom.length < 3) {
    const titled = (info.services ?? []).filter((s) => s.title?.trim());
    const label =
      titled.length === 0
        ? 'Services empty'
        : custom.length === 0
          ? 'Still demo on services'
          : `Services ${custom.length}/3`;
    gaps.push({ kind: 'services', label });
  }
  const phoneOk = !blank(info.phone) && !demo.has('phone');
  const emailOk = !blank(info.email) && !demo.has('email');
  if (!phoneOk && !emailOk) {
    gaps.push({
      kind: 'contact',
      label: blank(info.phone) && blank(info.email) ? 'Phone or email empty' : 'Contact still demo',
    });
  }
  return gaps;
}

export function formatCallSheetGaps(gaps: CallSheetGap[]): string {
  if (gaps.length === 0) return 'Ready to spring';
  return gaps.map((g) => g.label).join(' · ');
}

/** First missing print-critical gap kind, or null when ready to spring. */
export function callSheetSpringGap(info: BusinessInfo, industry: string | undefined): CallSheetGapKind | null {
  return callSheetPrintGaps(info, industry)[0]?.kind ?? null;
}
